import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Client } from '@stomp/stompjs';
import { MapContainer, MapMarker } from 'components/map';
import { RoutePolyline } from 'components/map/types';
import { LocationLoading } from 'components/common/LocationLoading';
import { useMeetingDetail, useMeetingMembers } from 'hooks/useMeeting';
import { useUserLocation } from 'hooks/useUserLocation';
import { meetingStore } from 'store/meetingStore';
import { MeetingRepository } from 'repository/meetingRepository';
import { instance, ensureFreshAccessToken } from 'common/axios/axios';
import { getApiUrl, getGatewayUrl } from 'common/axios/getApiUrl';
import config from 'config/config';
import { isNativeApp, openNativeRealtimeLocation, syncNativeMeetingSchedule, removeNativeMeetingSchedule } from 'common/webview';
import { isInKorea } from 'common/utils/regionDetector';
import { getRoute, formatDuration, RouteStep } from 'common/utils/routeService';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import useSmoothMarkers from 'hooks/useSmoothMarkers';
import {
    IcPin, IcFlag, IcFinishFlag, IcMedal, IcCheck, IcRun, IcHourglass,
    IcClose, IcQuestion, IcLock, IcTurtle,
} from 'components/common/icons/EmojiIcons';
import { RaceProgressRow } from 'components/meeting/raceTypes';
// TODO: [SIMULATOR] — 삭제 시 이 import + 아래 SIMULATOR useEffect 블록 제거
import {
    SIMULATOR_ENABLED,
    SIMULATOR_TICK_MS,
    simulatorStartPoint,
    simulatorNextStep,
} from 'common/utils/locationSimulator';
import styles from './RealtimeLocation.module.css';
import { haversineMeters } from 'common/utils/geo';
import { Tabs, Button } from 'components/ds';
import { useSafeReturnSessions } from 'hooks/useSafeReturnSessions';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

type ViewMode = 'participants' | 'directions' | 'info';

// 인라인 SVG 아이콘 — 이모지 금지 (UI 심볼은 모두 SVG)
const IcArrowLeft = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);
const IcRefresh = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);
const IcCompass = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
);
const IcPinFilled = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

// 출발/도착은 SVG 깃발 아이콘(이모지 금지). 그 외 방향 안내는 타이포그래픽 화살표 글리프 유지.
const stepIcon = (type: RouteStep['type']): React.ReactNode => {
    switch (type) {
        case 'depart': return <IcFlag size={16} />;
        case 'arrive': return <IcFinishFlag size={16} />;
        case 'left': return '↰';
        case 'right': return '↱';
        case 'uturn': return '⤴';
        case 'straight': return '↑';
        case 'merge': return '⇉';
        case 'fork': return '⇈';
        case 'roundabout': return '⟳';
        default: return '•';
    }
};

const formatStepDistance = (m: number): string => {
    if (m < 1000) return `${Math.round(m)}m`;
    return `${(m / 1000).toFixed(1)}km`;
};

interface ParticipantLocation {
    id: number;
    name: string;
    profileImgUrl?: string;
    status: 'arriving' | 'arrived' | 'not-started' | 'not-attending' | 'undecided' | 'no-sharing';
    distance: string;
    eta: string;
    etaSeconds: number;
    attendanceStatus: string;
    isLocationSharing: boolean;
    showOnMap: boolean;
    latitude?: number;
    longitude?: number;
}

const MARKER_COLORS = ['#4285F4', '#34A853', '#9C27B0', '#FF9800', '#E91E63', '#00BCD4'];

export const RealtimeLocation = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { data: meetingData } = useMeetingDetail();
    const { data: membersData } = useMeetingMembers();

    const currentUserNo = Number(sessionStorage.getItem('userNo')) || 1;
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [isListExpanded, setIsListExpanded] = useState(false);
    // rl-sheet 실제 높이 측정 — 외부 플로팅 요소가 시트 위로 배치되도록 CSS 변수로 노출
    const rlSheetRef = React.useRef<HTMLDivElement | null>(null);
    const rlWrapperRef = React.useRef<HTMLDivElement | null>(null);
    // 그리기 전에 재야 합니다. useEffect 로 재면 첫 프레임은 CSS 기본값(42dvh)으로
    // 그려졌다가 실제 높이로 튑니다 — 화면이 큰 기기에서는 그 차이가 400px 가 넘습니다.
    React.useLayoutEffect(() => {
        const sheet = rlSheetRef.current;
        const wrapper = rlWrapperRef.current;
        if (!sheet || !wrapper) return;
        const update = () => {
            const h = sheet.getBoundingClientRect().height;
            wrapper.style.setProperty('--rl-sheet-h', `${Math.round(h)}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(sheet);
        return () => ro.disconnect();
    }, []);
    const [routeLines, setRouteLines] = useState<RoutePolyline[]>([]);
    const [routeSummaries, setRouteSummaries] = useState<{ name: string; duration: string; distance: string; color: string }[]>([]);
    // 참여자별 누적 경로 — wiz-lbs 의 lbs:path:DOKOCREW:{userNo}:{stayId} 스냅샷 + STOMP 으로 점 append.
    // 도착 시 frontend 가 거리 기반으로 polyline reset + segLocalIdx ++ (색상 변경).
    // ON/OFF 토글은 src/config/config.ts 의 config.PATH_LINES_ENABLED 한 곳만 변경 — 관련 동작 모두 일괄 토글.
    const [participantPaths, setParticipantPaths] = useState<Record<number, { points: { lat: number; lng: number }[]; segLocalIdx: number; stayId?: number }>>({});
    // Dead Reckoning + RAF 기반 끊김 없는 마커 애니메이션
    const smooth = useSmoothMarkers();
    const memberLocations = smooth.markers;

    // 포커스 대상 — 좌측 상단 패널에서 목적지/참여자 클릭 시 30m 스케일 zoom-in
    const [focusTarget, setFocusTarget] = useState<{ lat: number; lng: number; level: number; key: string } | null>(null);
    // '전체 보기' 트리거 — 증가할 때마다 KakaoMap 이 모든 마커(목적지+참여자) 를 뷰포트에 맞춤
    const [viewAllTrigger, setViewAllTrigger] = useState(0);
    const [shareLocation, setShareLocation] = useState(() => {
        return localStorage.getItem(`locationSharing_${meetingId}`) === 'true';
    });
    const [viewMode, setViewMode] = useState<ViewMode>('participants');
    const [showExternalMap, setShowExternalMap] = useState(false);
    const [myRoute, setMyRoute] = useState<{ line: RoutePolyline; steps: RouteStep[]; duration: number; distance: number } | null>(null);
    const [isRouteLoading, setIsRouteLoading] = useState(false);
    const { location: myLocation, fetchLocation } = useUserLocation();

    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
            meetingStore.onGetMeetingMembers(meetingId);
        }
    }, [meetingId]);

    // 진입 시 default focusTarget = 목적지. 그렇게 하지 않으면 fitBounds 가 발동해서 사용자 GPS(예: 서울)와
    // 여행 목적지(예: 후쿠오카) 를 모두 감싸는 광역 zoom-out 이 걸려 지도가 창녕군 근처 중간 지점에 잡히는
    // 회귀가 있었음. 사용자가 &quot;전체 보기/참여자&quot; 버튼을 명시적으로 누르면 그 때 setFocusTarget 이 override.
    const destFocusInitializedRef = useRef(false);
    useEffect(() => {
        if (destFocusInitializedRef.current) return;
        const lat = meetingData?.data?.latitude ? parseFloat(meetingData.data.latitude) : null;
        const lng = meetingData?.data?.longitude ? parseFloat(meetingData.data.longitude) : null;
        if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
            setFocusTarget({ lat, lng, level: 3, key: `dest-init-${meetingId}` });
            destFocusInitializedRef.current = true;
        }
    }, [meetingId, meetingData]);

    // 마운트 시 path snapshot 1회 fetch — wiz-lbs 의 lbs:path:DOKOCREW:{userNo}:{stayId} 의 점들을 폴리라인 초기화.
    // 표시 ON/OFF 는 config.PATH_LINES_ENABLED 로 일괄 토글. backend API 는 항상 데이터 정상 반환.
    useEffect(() => {
        if (!meetingId) return;
        if (!config.PATH_LINES_ENABLED) return;
        let cancelled = false;
        instance.get(`${getApiUrl('meetingDetail')}/${meetingId}/location-paths`)
            .then(res => {
                if (cancelled) return;
                const map: Record<string, any> = res?.data?.data || {};
                const next: Record<number, { points: { lat: number; lng: number }[]; segLocalIdx: number; stayId?: number }> = {};
                Object.keys(map).forEach(k => {
                    const userNo = Number(k);
                    const entry = map[k] || {};
                    const stayId = typeof entry.stayId === 'number' ? entry.stayId : undefined;
                    const pts: any[] = Array.isArray(entry.points) ? entry.points : [];
                    next[userNo] = {
                        stayId,
                        segLocalIdx: 0,
                        points: pts
                            .filter(p => typeof p.lat === 'number' && typeof p.lng === 'number')
                            .map(p => ({ lat: p.lat, lng: p.lng })),
                    };
                });
                setParticipantPaths(next);
            })
            .catch(() => { /* snapshot 실패는 STOMP 로 채워가면 됨 */ });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meetingId]);

    // ─── 디버그 훅 — 브라우저 콘솔에서 마커 이동 시뮬레이션 ───
    // 사용 예:
    //   __simulate()                               // 현재 위치에서 북쪽으로 30m씩 5s 간격 20회
    //   __simulate({ steps: 30, intervalMs: 3000 })// 3초 간격 30회
    //   __simulate({ dlat: 0, dlng: 30/111000 })   // 동쪽으로
    //   __smoothUpdate(37.50, 127.01)              // 즉시 한 점 주입
    //   __stopSimulate()                           // 중단
    //
    // 주의 — deps 에 smooth/myLocation 을 넣으면 마커 업데이트→리렌더→effect 재실행→
    //       cleanup 이 setTimeout 을 clear 해서 "1번만 발송" 되던 회귀가 있었음.
    //       최신 값은 ref 로 보관하고 effect 는 mount 1회만 실행.
    const simTimerRef = useRef<any>(null);
    const smoothRef = useRef(smooth);
    const myLocRef = useRef(myLocation);
    const myNoRef = useRef(currentUserNo);
    smoothRef.current = smooth;
    myLocRef.current = myLocation;
    myNoRef.current = currentUserNo;

    useEffect(() => {
        const w = window as any;
        w.__smoothUpdate = (lat: number, lng: number, un?: number) => {
            smoothRef.current.updateMarker(un ?? myNoRef.current, lat, lng);
        };
        w.__stopSimulate = () => {
            if (simTimerRef.current) { clearTimeout(simTimerRef.current); simTimerRef.current = null; }
        };
        w.__simulate = (opts: any = {}) => {
            w.__stopSimulate();
            const baseLat = opts.lat ?? myLocRef.current?.lat ?? 37.5;
            const baseLng = opts.lng ?? myLocRef.current?.lng ?? 127.0;
            const dlat = opts.dlat ?? (30 / 111000);
            const dlng = opts.dlng ?? 0;
            const intervalMs = opts.intervalMs ?? 5000;
            const steps = opts.steps ?? 20;
            const targetUserNo = opts.userNo ?? myNoRef.current;
            let i = 0;
            const run = () => {
                const lat = baseLat + i * dlat;
                const lng = baseLng + i * dlng;
                smoothRef.current.updateMarker(targetUserNo, lat, lng);
                console.log('[simulate]', i + 1, '/', steps, 'userNo:', targetUserNo, 'lat:', lat.toFixed(6), 'lng:', lng.toFixed(6));
                i++;
                if (i < steps) simTimerRef.current = setTimeout(run, intervalMs);
            };
            run();
            return { started: true, steps, intervalMs, baseLat, baseLng };
        };
        return () => {
            if (simTimerRef.current) clearTimeout(simTimerRef.current);
            delete w.__smoothUpdate;
            delete w.__simulate;
            delete w.__stopSimulate;
        };
    }, []);

    const stompClientRef = useRef<Client | null>(null);

    // STOMP 디버그 콘솔 — 폰 화면에서 logcat 대용. setInterval/콜백 closure 가
    // 안전하게 잡도록 ref 로 push 헬퍼를 노출한다.
    type StompLog = { time: string; tag: 'DBG' | 'CONN' | 'MSG' | 'ERR' | 'INFO'; text: string };
    const [stompLogs, setStompLogs] = useState<StompLog[]>([]);
    const [debugOpen, setDebugOpen] = useState(false);
    const [copyState, setCopyState] = useState<'idle' | 'ok' | 'fail'>('idle');
    const [nativeSummary, setNativeSummary] = useState<string>('');
    const [nativeSummaryAt, setNativeSummaryAt] = useState<number | null>(null);
    const pushStompLogRef = useRef((tag: StompLog['tag'], text: string) => {
        const time = new Date().toISOString().slice(11, 23);
        const trimmed = text.length > 400 ? text.slice(0, 400) + '…' : text;
        setStompLogs(prev => {
            const next = prev.length >= 200 ? prev.slice(prev.length - 199) : prev.slice();
            next.push({ time, tag, text: trimmed });
            return next;
        });
    });

    // 네이티브 측 측위 통계 polling (디버그 패널 열린 동안만)
    useEffect(() => {
        if (!debugOpen) return;
        if (!isNativeApp()) return;
        const ask = () => {
            try {
                window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'GET_LOCATION_DEBUG_PUSH', data: {} }));
            } catch { /* ignore */ }
        };
        ask();
        const id = setInterval(ask, 5000);
        return () => clearInterval(id);
    }, [debugOpen]);

    // 네이티브 LOCATION_DEBUG_DATA 수신
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            try {
                const raw = typeof e.data === 'string' ? e.data : JSON.stringify(e.data);
                if (!raw.includes('LOCATION_DEBUG_DATA')) return;
                const parsed = JSON.parse(raw);
                if (parsed?.type !== 'LOCATION_DEBUG_DATA') return;
                const summary = parsed?.data?.summary;
                if (typeof summary === 'string') {
                    setNativeSummary(summary);
                    setNativeSummaryAt(Date.now());
                }
            } catch { /* ignore */ }
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler as EventListener);
        return () => {
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler as EventListener);
        };
    }, []);

    const copyStompLogs = async () => {
        const stompPart = stompLogs.map(l => `${l.time} [${l.tag}] ${l.text}`).join('\n');
        const nativePart = nativeSummary
            ? `=== NATIVE STATS (updated ${nativeSummaryAt ? new Date(nativeSummaryAt).toISOString().slice(11, 19) : '?'}) ===\n${nativeSummary}\n\n=== STOMP LOGS ===\n`
            : '';
        const text = nativePart + stompPart;
        let ok = false;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                ok = true;
            } else {
                // WebView/HTTP 환경 fallback
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                ok = document.execCommand('copy');
                document.body.removeChild(ta);
            }
        } catch {
            ok = false;
        }
        setCopyState(ok ? 'ok' : 'fail');
        setTimeout(() => setCopyState('idle'), 1500);
    };

    // smooth 객체 자체는 매 렌더마다 새 참조(setMarkers 로 30fps 리렌더) 이므로
    // deps 에 직접 넣으면 초기 로드/STOMP 가 30fps 로 재실행됨 (/location/current 폭주).
    // 내부 콜백은 useSmoothMarkers 안에서 useCallback 으로 stable 하므로 구조분해로 분리.
    const { updateMarker: smoothUpdate, resetMarker: smoothReset } = smooth;

    // 초기 위치 일괄 로드 + STOMP WebSocket 구독
    useEffect(() => {
        if (!meetingId) return;

        const userNo = currentUserNo;

        // 1. 초기 위치 일괄 조회 (REST)
        instance.get(`${getApiUrl('meetingDetail')}/${meetingId}/location/current`)
            .then(res => {
                const locations = res?.data?.data;
                if (Array.isArray(locations)) {
                    const targetUserNos: number[] = [];
                    locations.forEach((loc: any) => {
                        if (loc.latitude && loc.longitude && loc.userNo) {
                            // 초기 배치 — 애니메이션 없이 즉시
                            smoothReset(loc.userNo, Number(loc.latitude), Number(loc.longitude));
                            // 위치측위 로그 — 본인 제외
                            if (Number(loc.userNo) !== userNo) {
                                targetUserNos.push(Number(loc.userNo));
                            }
                        }
                    });
                    // 위치 확인 로그 기록 (서버가 (viewer,target,meeting,date,type) UPSERT 하므로 중복 호출 무해)
                    if (targetUserNos.length > 0) {
                        import('repository/locationViewLogRepository').then(({ LocationViewLogRepository }) => {
                            LocationViewLogRepository.onRecordViews(meetingId, targetUserNos, 'REALTIME')
                                .catch(() => { /* 로그 실패는 무시 */ });
                        });
                    }
                }
            })
            .catch(() => { /* 초기 로드 실패 — STOMP으로 수신 대기 */ });

        // 2. STOMP WebSocket 연결 — Sec-WebSocket-Protocol smuggling 으로 토큰 전달
        // 게이트웨이 TokenExtractor 가 추출 → JWT 검증 → X-User-No 주입 → dokocrew-api HandshakeInterceptor 가 세션에 적재
        // beforeConnect 로 매 (재)연결 직전 토큰 fresh 보장 — stale 토큰으로 무한 reconnect 회귀 방지
        const wsUrl = `${getGatewayUrl()}/dokocrew/ws`.replace(/^http/, 'ws');
        let cachedToken: string | null = null;
        // 첫 onConnect 는 위 초기 REST 와 중복이므로 skip, 재연결부터만 보강 polling.
        let hasConnectedBefore = false;
        // user별 마지막 수신 timestamp — STOMP 메시지 vs REST polling 간 stale 검출용.
        // 재연결 직후 REST polling 응답이 STOMP 메시지보다 stale 한 경우 마커가 과거로 튐.
        // 메시지 수신 시 timestamp 기록하고, REST polling 적용 시 더 새로운 것만 반영.
        const lastRecvAt = new Map<number, number>();
        const client = new Client({
            webSocketFactory: () => {
                // beforeConnect 와 race 회피: cachedToken 미설정 시 localStorage 즉시 조회
                const token = cachedToken || localStorage.getItem('accessToken');
                if (!token) console.warn('[STOMP] 토큰 없음 — 익명 연결 시도');
                return new WebSocket(
                    wsUrl,
                    token ? ['v12.stomp', `bearer.${token}`] : ['v12.stomp'],
                );
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: (str) => {
                console.log('[STOMP-loc]', str);
                pushStompLogRef.current('DBG', str);
            },
            beforeConnect: async () => {
                cachedToken = await ensureFreshAccessToken();
                pushStompLogRef.current('INFO', cachedToken ? `token refreshed (len=${cachedToken.length})` : 'token MISSING');
            },
            onConnect: (frame) => {
                pushStompLogRef.current('CONN', `connected ${frame.headers['user-name'] || ''}`);
                // 위치 토픽 구독 — 서버가 CONNECT 단계 JWT 에서 추출한 userNo 로 모임 멤버십 검증
                client.subscribe(
                    `/topic/meeting/${meetingId}/location`,
                    (message) => {
                        try {
                            const update = JSON.parse(message.body);
                            pushStompLogRef.current('MSG', `userNo=${update.userNo} ${update.latitude?.toFixed?.(5)},${update.longitude?.toFixed?.(5)}`);
                            if (update.latitude && update.longitude && update.userNo) {
                                smoothUpdate(update.userNo, update.latitude, update.longitude);
                                // 메시지 수신 시각 기록 — 재연결 후 REST polling 의 stale 데이터 차단용.
                                lastRecvAt.set(Number(update.userNo), Date.now());

                                // 폴리라인 누적 — feature flag 로 쉽게 끌 수 있음.
                                if (config.PATH_LINES_ENABLED) {
                                    const userNo = Number(update.userNo);
                                    const newPoint = { lat: update.latitude, lng: update.longitude };
                                    setParticipantPaths(prev => {
                                        const cur = prev[userNo];
                                        if (!cur) {
                                            return { ...prev, [userNo]: { points: [newPoint], segLocalIdx: 0 } };
                                        }
                                        return { ...prev, [userNo]: { ...cur, points: [...cur.points, newPoint] } };
                                    });
                                }
                            }
                        } catch {
                            // 메시지 파싱 실패 무시
                            pushStompLogRef.current('ERR', `parse fail: ${message.body.slice(0, 80)}`);
                        }
                    }
                );
                pushStompLogRef.current('INFO', `subscribe /topic/meeting/${meetingId}/location`);

                // 재연결 후 누락 가능성 있는 메시지 보강 — REST 한 번 polling.
                // STOMP 으로 이미 받은 사용자는 REST 응답이 stale 일 수 있어 skip.
                // (reconnect 직후 REST 도달 시점이 마지막 STOMP 수신보다 늦을 때 마커가
                //  과거 좌표로 튀는 회귀 방지)
                if (hasConnectedBefore) {
                    pushStompLogRef.current('INFO', 'reconnect → REST polling /location/current');
                    const reconnectAt = Date.now();
                    instance.get(`${getApiUrl('meetingDetail')}/${meetingId}/location/current`)
                        .then(res => {
                            const locations = res?.data?.data;
                            if (Array.isArray(locations)) {
                                locations.forEach((loc: any) => {
                                    if (!loc.latitude || !loc.longitude || !loc.userNo) return;
                                    const userNo = Number(loc.userNo);
                                    const lastMsgAt = lastRecvAt.get(userNo) ?? 0;
                                    // 재연결 이후 STOMP 메시지를 이미 받은 사용자는 skip
                                    if (lastMsgAt >= reconnectAt) return;
                                    smoothUpdate(userNo, Number(loc.latitude), Number(loc.longitude));
                                });
                            }
                        })
                        .catch(() => { /* 다음 STOMP 메시지로 자연 보강 */ });
                }
                hasConnectedBefore = true;
            },
            onStompError: (frame) => {
                console.error('STOMP 에러:', frame.headers['message']);
                pushStompLogRef.current('ERR', frame.headers['message'] || 'stomp error');
            },
            onWebSocketClose: (evt) => {
                pushStompLogRef.current('ERR', `ws close code=${evt?.code} reason=${evt?.reason || ''}`);
            },
        });

        client.activate();
        stompClientRef.current = client;

        // 멤버 목록은 30초마다 갱신 (위치는 STOMP으로 실시간 수신)
        const memberInterval = setInterval(() => {
            meetingStore.onGetMeetingMembers(meetingId);
        }, 30000);

        return () => {
            clearInterval(memberInterval);
            // STOMP 연결 해제
            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
                stompClientRef.current = null;
            }
        };
    }, [meetingId, currentUserNo, smoothUpdate, smoothReset]);

    // 외부에서 destinationOverride 전달 받음 — 홈 today card / 모임 상세 미니지도 클릭 시
    // sub_place 좌표를 destination 으로 보내는 경로 (집결지가 아닌 "다음 이동 장소" 기준 위치 추적)
    const routeLocation = useLocation();
    const destinationOverride = (routeLocation.state as any)?.destinationOverride as { lat: number; lng: number; name?: string } | undefined;

    // 추가 장소 목록 fetch — 집결지 + sub_place 카드를 destination 후보로 노출
    const [subPlacesForDest, setSubPlacesForDest] = useState<any[]>([]);
    useEffect(() => {
        if (!meetingId) return;
        let cancelled = false;
        (async () => {
            try {
                const res: any = await MeetingRepository.onGetSubPlaces(meetingId);
                const list = res?.data?.data || res?.data || [];
                if (!cancelled && Array.isArray(list)) setSubPlacesForDest(list);
            } catch { /* 무시 */ }
        })();
        return () => { cancelled = true; };
    }, [meetingId]);

    // destination 후보 목록 — 좌표 있는 항목만 (집결지 + sub_place)
    type DestItem = { key: string; name: string; lat: number; lng: number; category?: string };
    const allDestinations: DestItem[] = (() => {
        const list: DestItem[] = [];
        const pLat = meetingData?.data?.latitude ? parseFloat(meetingData.data.latitude) : null;
        const pLng = meetingData?.data?.longitude ? parseFloat(meetingData.data.longitude) : null;
        if (pLat && pLng) {
            list.push({ key: 'primary', name: meetingData?.data?.placeName || t('realtime.placeUndecided'), lat: pLat, lng: pLng, category: '집결' });
        }
        for (const sp of subPlacesForDest) {
            if (sp.category === 'HOTEL') continue;
            const lat = typeof sp.latitude === 'number' ? sp.latitude : (sp.latitude ? parseFloat(sp.latitude) : null);
            const lng = typeof sp.longitude === 'number' ? sp.longitude : (sp.longitude ? parseFloat(sp.longitude) : null);
            if (!lat || !lng) continue;
            list.push({ key: `sp-${sp.subPlaceId}`, name: sp.placeName, lat, lng, category: sp.category });
        }
        return list;
    })();

    // 사용자가 선택한 destination — initial 은 destinationOverride 좌표와 매칭되는 항목
    const initialDestKey = (() => {
        if (destinationOverride) {
            const match = allDestinations.find(d =>
                Math.abs(d.lat - destinationOverride.lat) < 0.0001 &&
                Math.abs(d.lng - destinationOverride.lng) < 0.0001
            );
            if (match) return match.key;
        }
        return allDestinations[0]?.key || null;
    })();
    const [selectedDestKey, setSelectedDestKey] = useState<string | null>(initialDestKey);
    // allDestinations 가 늦게 채워지는 경우 selectedDestKey 보정
    useEffect(() => {
        if (selectedDestKey && allDestinations.find(d => d.key === selectedDestKey)) return;
        if (initialDestKey) setSelectedDestKey(initialDestKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allDestinations.map(d => d.key).join(','), initialDestKey]);

    const activeDest = allDestinations.find(d => d.key === selectedDestKey)
        || (destinationOverride ? { key: 'override', name: destinationOverride.name || '', lat: destinationOverride.lat, lng: destinationOverride.lng, category: '' } : null);

    const meetingLocation = {
        name: activeDest?.name || meetingData?.data?.placeName || t('realtime.placeUndecided'),
        address: meetingData?.data?.placeAddress || '',
        latitude: activeDest ? String(activeDest.lat) : meetingData?.data?.latitude,
        longitude: activeDest ? String(activeDest.lng) : meetingData?.data?.longitude,
    };
    const centerLat = activeDest?.lat
        ?? (meetingData?.data?.latitude ? parseFloat(meetingData.data.latitude) : 37.5665);
    const centerLng = activeDest?.lng
        ?? (meetingData?.data?.longitude ? parseFloat(meetingData.data.longitude) : 126.9780);

    // ──────────────── TODO: [SIMULATOR] START ────────────────
    // 네이티브 실시간 위치 복구 후 이 블록 전체 삭제
    // 5초마다 30m씩 목적지 방향으로 접근하는 가짜 참여자 좌표를 smooth 훅으로 주입
    // (smooth 훅이 EMA 로 속도벡터를 계산해서 매 프레임 보간 렌더링)
    const simTargetsRef = useRef<Record<number, { lat: number; lng: number }>>({});
    // smoothUpdate / smoothReset 은 상단에서 이미 구조분해됨 — 여기선 그대로 사용
    // 참여자 키로 list 변경 감지 (배열 레퍼런스가 아닌 값으로 비교)
    const simParticipantsKey = (membersData?.data || [])
        .filter((m: any) => m.attendanceStatus === 'ATTENDING')
        .map((m: any) => Number(m.userNo || m.user_no))
        .filter((no: number) => no && no !== currentUserNo)
        .sort((a: number, b: number) => a - b)
        .join(',');
    useEffect(() => {
        if (!SIMULATOR_ENABLED) return;
        if (!meetingLocation.latitude || !meetingLocation.longitude) return;
        if (!simParticipantsKey) return;
        const destLat = parseFloat(meetingLocation.latitude);
        const destLng = parseFloat(meetingLocation.longitude);
        const simTargets = simParticipantsKey.split(',').map(Number);

        // 초기 위치 즉시 배치 (애니메이션 없이)
        simTargets.forEach((userNo: number) => {
            if (simTargetsRef.current[userNo]) return;
            const start = simulatorStartPoint({ latitude: destLat, longitude: destLng }, userNo);
            simTargetsRef.current[userNo] = { lat: start.latitude, lng: start.longitude };
            smoothReset(userNo, start.latitude, start.longitude);
        });

        const interval = setInterval(() => {
            simTargets.forEach((userNo: number) => {
                const current = simTargetsRef.current[userNo];
                if (!current) return;
                const step = simulatorNextStep(
                    { latitude: current.lat, longitude: current.lng },
                    { latitude: destLat, longitude: destLng },
                );
                simTargetsRef.current[userNo] = { lat: step.latitude, lng: step.longitude };
                // smooth 훅이 EMA 로 부드럽게 보간
                smoothUpdate(userNo, step.latitude, step.longitude);
            });
        }, SIMULATOR_TICK_MS);
        return () => clearInterval(interval);
    }, [
        meetingLocation.latitude,
        meetingLocation.longitude,
        simParticipantsKey,
        smoothUpdate,
        smoothReset,
    ]);
    // ──────────────── TODO: [SIMULATOR] END ────────────────

    // 해외 모임 → 네이티브 구글맵 위임 (모임 장소 좌표로 판단)
    useEffect(() => {
        if (!isNativeApp() || !meetingData?.data || !meetingId) return;
        const d = meetingData.data;
        if (!d.latitude || !d.longitude) return;
        const lat = parseFloat(d.latitude), lng = parseFloat(d.longitude);
        // 모임 장소가 한국이면 카카오맵 사용 → 위임 안 함
        if (isInKorea(lat, lng)) return;
        // 해외 모임 → 네이티브 구글맵으로 위임
        openNativeRealtimeLocation({ meetingId, destination: { name: d.placeName || '', address: d.placeAddress || '', lat, lng } });
        navigate(-1);
    }, [meetingData, meetingId, navigate]);

    const calcDistance = haversineMeters;

    // 측위 표시 윈도우 — 시작 60분 전 ~ 종료까지. 본인 좌표는 항상 표시 (지도에 본인 위치 필요).
    // 백엔드 broadcast 윈도우는 timezone 안전을 위해 ±12h 로 넓게 잡혀 있어 5h+ 전에도 다른 멤버 좌표가
    // 흘러들어옴 → 프론트에서 동일한 1h 윈도우로 한 번 더 가드해 사용자 기대와 어긋나지 않게 함.
    const _meetingStartMs = (() => {
        const s = meetingData?.data?.schedule?.startDatetime;
        return s ? new Date(s).getTime() : null;
    })();
    const _minutesUntilStartForGuard = _meetingStartMs
        ? Math.round((_meetingStartMs - Date.now()) / 60000)
        : null;
    const trackingActive = _minutesUntilStartForGuard == null || _minutesUntilStartForGuard <= 60;

    const participants: ParticipantLocation[] = (membersData?.data || []).map((member: any) => {
        const att = member.attendanceStatus || 'UNDECIDED';
        // 서버가 내려준 locationSharing 을 최우선. 없을 때만 로컬 캐시 fallback.
        const serverSharing = typeof member.locationSharing === 'boolean' ? member.locationSharing : undefined;
        const isSharing = serverSharing !== undefined
            ? serverSharing
            : localStorage.getItem(`locationSharing_${meetingId}_${member.userNo}`) !== 'false';
        const profileImgUrl = member.profileImageUrl || member.profileImgPath || undefined;

        // lbs-service에서 조회한 실제 위치
        const lbsLoc = memberLocations[member.userNo];
        // 측위 윈도우(시작 60분전~종료) 밖에서는 본인 외 다른 멤버 좌표 무시 — UX 가드.
        const showRemoteLoc = trackingActive || member.userNo === currentUserNo;
        const lat = showRemoteLoc ? lbsLoc?.lat : undefined;
        const lng = showRemoteLoc ? lbsLoc?.lng : undefined;

        let status: ParticipantLocation['status'] = 'no-sharing';
        let distance = '-', eta = '-', etaSeconds = 0;
        if (att === 'NOT_ATTENDING') status = 'not-attending';
        else if (att === 'UNDECIDED') status = 'undecided';
        else if (!isSharing) status = 'no-sharing';
        else if (!lat || !lng) status = 'not-started'; // 위치 공유 ON이지만 아직 위치 없음
        else {
            const dist = calcDistance(lat, lng, centerLat, centerLng);
            distance = dist < 100 ? t('realtime.arrived') : dist < 1000 ? t('realtime.meter', { n: Math.round(dist) }) : t('realtime.km', { n: (dist / 1000).toFixed(1) });
            status = dist < 100 ? 'arrived' : 'arriving';
            const mins = Math.round(dist / 83);
            etaSeconds = mins * 60;
            eta = mins <= 1 ? t('realtime.arrivingSoon') : t('realtime.minutes', { n: mins });
        }

        return {
            id: member.userNo,
            name: (() => { try { return decodeURIComponent(member.nickname || ''); } catch { return member.nickname || String(member.userNo); } })(),
            profileImgUrl, status, distance, eta, etaSeconds, attendanceStatus: att, isLocationSharing: isSharing,
            // 공유 OFF / 불참 / 좌표 없음 → 지도 비표시
            showOnMap: !!lat && !!lng && att !== 'NOT_ATTENDING' && isSharing,
            latitude: lat, longitude: lng,
        };
    });

    // 레이스 진행 막대그래프 패널 (race_enabled 이고 시작 30분전~15분후)
    const raceEnabled = Boolean((meetingData?.data as any)?.raceEnabled);
    const meetingStartMs = (() => {
        const s = meetingData?.data?.schedule?.startDatetime;
        return s ? new Date(s).getTime() : null;
    })();
    const meetingEndMs = (() => {
        const s = meetingData?.data?.schedule?.endDatetime;
        return s ? new Date(s).getTime() : null;
    })();

    // 활성 안전 귀가 세션 존재 여부 — 있으면 모임 종료해도 측위 화면 계속 동작
    const hasActiveSafeReturn = useSafeReturnSessions(meetingId).length > 0;

    // 주최자 명시 종료 시각 (endedAt) — 일정보다 미리 종료 처리한 경우 schedule 시간 무관 종료로 간주.
    const endedAt = (meetingData?.data as any)?.endedAt;
    const endedAtMs = endedAt ? new Date(endedAt).getTime() : null;

    // 모임 종료 여부 — endDatetime 지나거나 없을 때 startDatetime+3h 기준
    // 단, 활성 safe-return 세션이 있으면 종료로 취급하지 않음 (귀가 동안 위치 추적 유지)
    const meetingEnded = (() => {
        if (hasActiveSafeReturn) return false;
        // 주최자 미리 종료 — 즉시 종료로 인식하여 참여자 위치 마커 hide
        if (endedAtMs && Date.now() >= endedAtMs) return true;
        if (meetingEndMs) return Date.now() > meetingEndMs;
        if (meetingStartMs) return Date.now() > meetingStartMs + 3 * 60 * 60 * 1000;
        return false;
    })();
    const minutesUntilStart = meetingStartMs ? Math.round((meetingStartMs - Date.now()) / 60000) : null;
    const isRaceWindow = raceEnabled && minutesUntilStart != null && minutesUntilStart <= 30 && minutesUntilStart >= -15;
    const startDistRef = useRef<Record<number, number>>({});
    const progressRows: RaceProgressRow[] = React.useMemo(() => {
        if (!isRaceWindow) return [];
        const rows = participants
            .filter(p => p.attendanceStatus === 'ATTENDING')
            .map(p => {
                let currentDistM = 0;
                let arrived = false;
                if (p.latitude != null && p.longitude != null) {
                    currentDistM = calcDistance(p.latitude, p.longitude, centerLat, centerLng);
                    arrived = currentDistM < 150;
                }
                // 처음 관측된 거리를 start 로 저장 (재방문시에도 유지)
                const saved = startDistRef.current[p.id];
                if (!saved || saved < currentDistM) startDistRef.current[p.id] = currentDistM;
                return {
                    userNo: p.id,
                    nickname: p.name,
                    profileImgUrl: p.profileImgUrl,
                    startDistM: startDistRef.current[p.id] || currentDistM,
                    currentDistM,
                    arrived,
                    isMe: p.id === currentUserNo,
                    rank: 0,
                    isLast: false,
                };
            });
        rows.sort((a, b) => {
            if (a.arrived && !b.arrived) return -1;
            if (!a.arrived && b.arrived) return 1;
            return a.currentDistM - b.currentDistM;
        });
        rows.forEach((r, i) => { r.rank = i + 1; });
        if (rows.length > 1) {
            const lastIdx = rows.length - 1;
            if (!rows[lastIdx].arrived) rows[lastIdx].isLast = true;
        }
        return rows;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participants, centerLat, centerLng, isRaceWindow]);

    // 진입 즉시 내 위치 조회 — 단, 모임 시작이 임박하지 않으면 측위/노출 의미 없음.
    // shareLocation 토글이 ON 이라도 시작 60분 전부터만 GPS prompt + 마커 노출.
    const isLocationActiveWindow = (
        meetingEnded
            ? false
            : (minutesUntilStart == null || minutesUntilStart <= 60)
    );
    useEffect(() => {
        if (!isLocationActiveWindow) return;
        if (!myLocation) fetchLocation();
    }, [isLocationActiveWindow, myLocation, fetchLocation]);

    // 길찾기 모드 전용: 내 위치 → 목적지 경로 + 턴바이턴
    useEffect(() => {
        if (viewMode !== 'directions') { setMyRoute(null); setIsRouteLoading(false); return; }
        if (!myLocation || !meetingLocation.latitude || !meetingLocation.longitude) return;
        let cancelled = false;
        setIsRouteLoading(true);
        getRoute(
            { lat: myLocation.lat, lng: myLocation.lng },
            { lat: centerLat, lng: centerLng }
        ).then(route => {
            if (cancelled || !route) { setIsRouteLoading(false); return; }
            setMyRoute({
                line: { coordinates: route.coordinates, color: '#667eea', strokeWidth: 5 },
                steps: route.steps || [],
                duration: route.duration,
                distance: route.distance,
            });
            setIsRouteLoading(false);
        }).catch(() => { if (!cancelled) setIsRouteLoading(false); });
        return () => { cancelled = true; };
    }, [viewMode, myLocation, centerLat, centerLng]);

    const onMap = participants.filter(p => p.showOnMap);
    const sorted = [...participants].sort((a, b) => {
        const o: Record<string, number> = { arrived: 0, arriving: 1, 'not-started': 2, 'no-sharing': 3, undecided: 4, 'not-attending': 5 };
        return (o[a.status] ?? 9) - (o[b.status] ?? 9);
    });

    // 위치공유가 OFF 면 내 마커를 지도에 표시하지 않음 (다른 참여자에게도 안 보내지만
    // 본인 화면에서도 즉시 가려야 한다는 UX 요구).
    // 추가로 시작 60분 전 이전 (먼 미래 예정 모임) 에는 GPS 마커 노출 의미 없음 — 가린다.
    const showMyMarker = shareLocation && !meetingEnded && isLocationActiveWindow;
    const mapMarkers: MapMarker[] = meetingEnded
        ? [
            // 모임 종료 후엔 목적지(모임 장소) 만 표시 — 참여자/내 위치 마커 없음
            { id: 'dest', lat: centerLat, lng: centerLng, label: '', tooltip: meetingLocation.name, isDestination: true },
        ]
        : viewMode === 'directions'
            ? [
                { id: 'dest', lat: centerLat, lng: centerLng, label: '', tooltip: meetingLocation.name, isDestination: true },
                ...(showMyMarker && myLocation ? [{
                    id: 'me' as const, lat: myLocation.lat, lng: myLocation.lng,
                    label: t('realtime.me'), color: '#667eea', tooltip: t('realtime.myLocation'),
                }] : []),
            ]
            : [
                { id: 'dest', lat: centerLat, lng: centerLng, label: '', tooltip: meetingLocation.name, isDestination: true },
                // 참여자 모드에서도 내 위치를 즉시 표시 (서버 STOMP 위치 없으면 GPS fallback)
                // — 단 공유 OFF 면 숨김
                ...(!showMyMarker || memberLocations[currentUserNo]
                    ? []
                    : myLocation ? [{
                        id: 'me' as const, lat: myLocation.lat, lng: myLocation.lng,
                        label: t('realtime.me'), color: '#667eea', tooltip: t('realtime.myLocation'),
                    }] : []),
                // 색상은 p.id 로 결정해 참여자 순서 변동(위치 비동기 도착)에도 같은 유저에게 같은 색 유지
                // — 색 바뀌면 contentHash 변경돼 KakaoMap 이 마커를 재생성(=끊김)함
                ...onMap.filter(p => p.latitude && p.longitude).map((p) => ({
                    id: p.id, lat: p.latitude!, lng: p.longitude!, label: p.name[0],
                    color: MARKER_COLORS[Math.abs(Number(p.id)) % MARKER_COLORS.length],
                    tooltip: `${p.name} - ${p.distance}`,
                    profileImgUrl: p.profileImgUrl,
                })),
            ];

    // 도착 판정 — 사용자의 마지막 점이 segLocalIdx 가 가리키는 destination 50m 이내면
    // polyline reset + segLocalIdx ++. allDestinations 는 메인 → sub_places 순서.
    useEffect(() => {
        if (!config.PATH_LINES_ENABLED) return;
        if (!allDestinations || allDestinations.length === 0) return;
        const ARRIVAL_M = 50;
        let needUpdate = false;
        const updates: Record<number, { points: { lat: number; lng: number }[]; segLocalIdx: number; stayId?: number }> = {};
        Object.entries(participantPaths).forEach(([userNoStr, v]) => {
            if (!v || v.points.length === 0) return;
            const target = allDestinations[v.segLocalIdx];
            if (!target) return;
            const last = v.points[v.points.length - 1];
            const distM = calcDistance(last.lat, last.lng, target.lat, target.lng);
            if (distM < ARRIVAL_M) {
                // 도착 — 폴리라인 reset, segLocalIdx ++, 마지막 점만 새 segment 의 시작점으로
                updates[Number(userNoStr)] = {
                    ...v,
                    points: [last],
                    segLocalIdx: v.segLocalIdx + 1,
                };
                needUpdate = true;
            }
        });
        if (needUpdate) {
            setParticipantPaths(prev => ({ ...prev, ...updates }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantPaths, allDestinations.length]);

    // 참여자별 누적 경로 polyline. 도착 시 frontend 가 거리로 판정해 reset (위 useEffect).
    // 색상: 사용자 base + segLocalIdx 시프트 — 도착할 때마다 색 변경.
    const participantRouteLines: RoutePolyline[] = config.PATH_LINES_ENABLED && !meetingEnded && viewMode === 'participants'
        ? Object.entries(participantPaths)
            .filter(([, v]) => v && v.points.length >= 2)
            .map(([userNoStr, v]) => {
                const userNo = Number(userNoStr);
                const baseIdx = Math.abs(userNo) % MARKER_COLORS.length;
                const colorIdx = (baseIdx + v.segLocalIdx) % MARKER_COLORS.length;
                return {
                    coordinates: v.points.map(p => [p.lat, p.lng] as [number, number]),
                    color: MARKER_COLORS[colorIdx],
                    strokeWidth: 3,
                };
            })
        : [];

    const mapRoutes: RoutePolyline[] = viewMode === 'directions'
        ? (myRoute ? [myRoute.line] : [])
        : [...participantRouteLines, ...routeLines];

    // 경로 가져오기 — 길찾기 탭 외에는 외부 API 호출 금지 (요금 방지)
    useEffect(() => {
        if (viewMode !== 'directions') {
            setRouteLines([]);
            setRouteSummaries([]);
        }
    }, [viewMode]);

    const toggleShare = async () => {
        if (!meetingId) return;
        const newVal = !shareLocation;
        // 낙관적 UI
        setShareLocation(newVal);
        localStorage.setItem(`locationSharing_${meetingId}`, String(newVal));

        // 서버 myLocationSharing 업데이트 — 이게 없으면 다음 진입 시 서버 false 가
        // localStorage 를 덮어써 토글이 초기화됨
        try {
            await MeetingRepository.onPutMyMembership(meetingId, { locationSharing: newVal });
            meetingStore.onGetMeetingMembers(meetingId);
        } catch (e) {
            // 실패 시 롤백
            setShareLocation(!newVal);
            localStorage.setItem(`locationSharing_${meetingId}`, String(!newVal));
            return;
        }

        if (isNativeApp()) {
            if (newVal) {
                syncNativeMeetingSchedule(meetingId, meetingData?.data);
            } else if (!newVal) {
                removeNativeMeetingSchedule(meetingId);   // 이 모임만 해제
            }
        }
    };

    const statusLabel = (s: ParticipantLocation['status']) => {
        // icon 은 SVG 컴포넌트(이모지 금지). 도보/일시정지 전용 아이콘이 없어
        // arriving→IcRun(이동중), not-started→IcHourglass(대기) 로 근사.
        const m: Record<string, { text: string; color: string; icon: React.ReactNode }> = {
            arrived: { text: t('realtime.statusArrived'), color: '#4CAF50', icon: <IcCheck size={13} /> },
            arriving: { text: t('realtime.statusArriving'), color: '#FF9800', icon: <IcRun size={13} /> },
            'not-started': { text: t('realtime.statusNotStarted'), color: '#999', icon: <IcHourglass size={13} /> },
            'not-attending': { text: t('realtime.statusNotAttending'), color: '#F44336', icon: <IcClose size={13} /> },
            undecided: { text: t('realtime.statusUndecided'), color: '#FF9800', icon: <IcQuestion size={13} /> },
            'no-sharing': { text: t('realtime.statusNoSharing'), color: '#999', icon: <IcLock size={13} /> },
        };
        return m[s] || { text: '?', color: '#999', icon: null };
    };

    return (
        <div className={cls("rl-wrapper")} ref={rlWrapperRef}>
            <LocationLoading visible={isLocationLoading} />

            {/* destination chip 리스트 — 집결지 + sub_place 좌표 후보. 클릭 시 지도 destination 변경 */}
            {allDestinations.length > 1 && (
                <div className={cls("rl-dest-chips")}>
                    {allDestinations.map(d => {
                        const active = d.key === selectedDestKey;
                        return (
                            <button
                                key={d.key}
                                type="button"
                                className={cls(active ? 'rl-dest-chip active' : 'rl-dest-chip')}
                                onClick={() => {
                                    setSelectedDestKey(d.key);
                                    setFocusTarget({ lat: d.lat, lng: d.lng, level: 2, key: `dest-${d.key}-${Date.now()}` });
                                }}
                            >
                                {d.category && <span className={cls("rl-dest-chip-cat")}>{d.category}</span>}
                                {d.name || t('meeting.markerPlace')}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 지도 (전체 화면) */}
            <div className={cls("rl-map")}>
                <MapContainer center={{ lat: centerLat, lng: centerLng }} markers={mapMarkers} routes={mapRoutes} height="100%" zoomLevel={4} showMyLocationBtn fitBounds={mapMarkers.length > 1 || mapRoutes.length > 0} focusTarget={focusTarget} viewAllTrigger={viewAllTrigger} />

                {/* 모임 종료 안내 오버레이 — 참여자 마커 모두 비표시, 모임 장소만 표시 */}
                {meetingEnded && (
                    <div className={cls("rl-ended-banner")} role="status" aria-live="polite">
                        <span className={cls("rl-ended-icon")} aria-hidden>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </span>
                        <span className={cls("rl-ended-text")}>{t('realtime.meetingEnded')}</span>
                    </div>
                )}

                {/* 좌측 상단 포커스 패널 — 전체보기/목적지/참여자 별로 zoom-in */}
                <div className={cls("rl-focus-panel")}>
                    <button
                        type="button"
                        className={cls(`rl-focus-item view-all ${focusTarget === null ? 'active' : ''}`)}
                        onClick={() => {
                            setFocusTarget(null);
                            setViewAllTrigger(v => v + 1);
                        }}
                        title={t('realtime.viewAll')}
                    >
                        <span className={cls("rl-focus-ic")}>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <polyline points="15 3 21 3 21 9" />
                                <polyline points="9 21 3 21 3 15" />
                                <line x1="21" y1="3" x2="14" y2="10" />
                                <line x1="3" y1="21" x2="10" y2="14" />
                            </svg>
                        </span>
                        <span className={cls("rl-focus-label")}>{t('realtime.viewAll')}</span>
                    </button>
                    <button
                        type="button"
                        className={cls(`rl-focus-item dest ${focusTarget?.key?.startsWith('dest-') ? 'active' : ''}`)}
                        onClick={() => setFocusTarget({ lat: centerLat, lng: centerLng, level: 2, key: `dest-${Date.now()}` })}
                        title={t('realtime.focusDestination')}
                    >
                        <span className={cls("rl-focus-ic")}>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                        </span>
                        <span className={cls("rl-focus-label")}>{t('realtime.destShort')}</span>
                    </button>
                    {/* 내 위치 — 지도에 내 마커가 보이는 동안 항상 노출 (서버 위치 없어도 GPS 좌표로 포커스) */}
                    {(() => {
                        const me = participants.find(p => p.id === currentUserNo);
                        const meLat = me?.latitude ?? (showMyMarker && myLocation ? myLocation.lat : undefined);
                        const meLng = me?.longitude ?? (showMyMarker && myLocation ? myLocation.lng : undefined);
                        if (meLat == null || meLng == null) return null;
                        const meName = me?.name || t('realtime.me');
                        return (
                            <button
                                type="button"
                                className={cls(`rl-focus-item me ${focusTarget?.key?.startsWith('p-' + currentUserNo) ? 'active' : ''}`)}
                                onClick={() => setFocusTarget({ lat: meLat, lng: meLng, level: 2, key: `p-${currentUserNo}-${Date.now()}` })}
                                title={meName}
                            >
                                {me?.profileImgUrl ? (
                                    <img className={cls("rl-focus-avatar")} src={me.profileImgUrl} alt={meName} />
                                ) : (
                                    <span className={cls("rl-focus-avatar fallback")}>{t('realtime.me')}</span>
                                )}
                                <span className={cls("rl-focus-label")}>{t('realtime.me')}</span>
                            </button>
                        );
                    })()}
                    {/* 좌표 없는 참여자 (공유 전, 미참석 등) 도 버튼 노출 — 좌표 있으면 클릭 시 zoom, 없으면 비활성 */}
                    {sorted.filter(p => p.id !== currentUserNo && p.attendanceStatus !== 'NOT_ATTENDING').map(p => {
                        const hasLoc = p.latitude != null && p.longitude != null;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                className={cls(`rl-focus-item ${focusTarget?.key?.startsWith('p-' + p.id) ? 'active' : ''} ${!hasLoc ? 'no-loc' : ''}`)}
                                onClick={() => {
                                    if (hasLoc) setFocusTarget({ lat: p.latitude!, lng: p.longitude!, level: 2, key: `p-${p.id}-${Date.now()}` });
                                }}
                                disabled={!hasLoc}
                                title={hasLoc ? p.name : `${p.name} ${t('race.beforeShare')}`}
                            >
                                {p.profileImgUrl ? (
                                    <img className={cls("rl-focus-avatar")} src={p.profileImgUrl} alt={p.name} />
                                ) : (
                                    <span className={cls("rl-focus-avatar fallback")}>{p.name[0] || '?'}</span>
                                )}
                                <span className={cls("rl-focus-label")}>{p.name}</span>
                            </button>
                        );
                    })}
                </div>

                {/* 레이스 진행 상황은 상단 오버레이 대신 하단 참여자 시트에 통합 (버튼 가림 방지) */}

                {/* 상단 바 */}
                <div className={cls("rl-top-bar")}>
                    <button className={cls("rl-back")} onClick={() => navigate(-1)} aria-label="back"><IcArrowLeft /></button>
                    <div className={cls("rl-top-info")}>
                        <div className={cls("rl-top-name")}>{meetingLocation.name}</div>
                        <div className={cls("rl-top-count")}>{t('realtime.sharingCount', { n: onMap.length, total: participants.length })}</div>
                    </div>
                    <button className={cls("rl-refresh")} onClick={() => {
                        if (viewMode === 'directions') { fetchLocation(true); }
                        else if (meetingId) { meetingStore.onGetMeetingMembers(meetingId); }
                    }} aria-label="refresh"><IcRefresh /></button>
                </div>

                {/* 모드 토글 */}
                {/* 모드 탭은 하단 시트 안으로 옮겼습니다 — 지도 위에 떠 있는 것을 줄입니다 */}

                {/* 경로 요약 팝업 (참여자 모드에서만) */}
                {viewMode === 'participants' && routeSummaries.length > 0 && (
                    <div className={cls("rl-route-summary")}>
                        {routeSummaries.map((rs, i) => (
                            <div key={i} className={cls("rl-route-item")}>
                                <span className={cls("rl-route-dot")} style={{ background: rs.color }}></span>
                                <span className={cls("rl-route-name")}>{rs.name}</span>
                                <span className={cls("rl-route-time")}>{rs.duration}</span>
                                <span className={cls("rl-route-dist")}>{rs.distance}</span>
                            </div>
                        ))}
                    </div>
                )}

            </div>

            {/* 레이스 진행판은 참여자 시트에 통합되었음 — 별도 플로팅 제거 */}

            {/* 하단 시트 — 모드별 분기 */}
            <div className={cls(`rl-sheet ${isListExpanded ? 'expanded' : ''}`)} ref={rlSheetRef}>
                {/* 드래그 핸들 */}
                {/* 내 위치 공유 — 스위치라서 탭 안에 넣지 않고 늘 보이는 자리에 둡니다 */}
                {!meetingEnded && (
                    <div className={cls("rl-share-toggle")} onClick={toggleShare}>
                        <span className={cls("rl-share-label")}><IcPinFilled /> {t('realtime.shareMyLocation')}</span>
                        <div className={cls(`rl-toggle ${shareLocation ? 'on' : ''}`)}>
                            <div className={cls("rl-toggle-knob")}></div>
                        </div>
                    </div>
                )}

                {/* 모드 탭 — 지도 위에 떠 있던 것을 여기로 내렸습니다 */}
                <div className={cls("rl-tabs")}>
                    <Tabs
                        ariaLabel={t('realtime.participantsTab')}
                        value={viewMode}
                        onChange={(v) => setViewMode(v as ViewMode)}
                        items={[
                            { value: 'participants', label: t('realtime.participantsTab'), count: participants.length },
                            { value: 'directions', label: t('realtime.directionsTab') },
                            { value: 'info', label: t('realtime.infoTab') },
                        ]}
                    />
                </div>

                <div className={cls("rl-sheet-handle")} onClick={() => setIsListExpanded(!isListExpanded)}>
                    <div className={cls("rl-handle-bar")}></div>
                    <div className={cls("rl-sheet-header")}>
                        <span className={cls("rl-sheet-title")}>
                            {viewMode === 'directions'
                                ? (myRoute ? `${t('realtime.directionsHeader', { duration: formatDuration(myRoute.duration), distance: formatStepDistance(myRoute.distance) })}` : `${t('realtime.directionsTab')}`)
                                : isRaceWindow && progressRows.length > 0
                                    ? `${t('realtime.raceTitle', { n: progressRows.length })}${minutesUntilStart != null ? (minutesUntilStart > 0 ? ` · ${t('realtime.raceMinBefore', { n: minutesUntilStart })}` : minutesUntilStart === 0 ? ` · ${t('realtime.raceStartNow')}` : ` · ${t('realtime.raceAfter', { n: Math.abs(minutesUntilStart) })}`) : ''}`
                                    : t('realtime.participantsTitle', { n: participants.length })}
                        </span>
                        <span className={cls("rl-sheet-toggle")}>{isListExpanded ? `▼ ${t('realtime.expandCollapse')}` : `▲ ${t('realtime.expandExpand')}`}</span>
                    </div>
                </div>

                {/* === 정보 === 측위 안내 · 목적지 · 레이스 요약 · 지도 앱 */}
                {viewMode === 'info' && (
                    <div className={cls("rl-info")}>
                        {/* 측위가 언제 시작되는지 — 지도 위에 떠 있던 안내를 여기로 모았습니다 */}
                        {minutesUntilStart != null && minutesUntilStart > 60 ? (
                            <p className={cls("rl-info-notice")}>
                                {(() => {
                                    const untilTrack = minutesUntilStart - 60;
                                    return t('realtime.beforeStartNoticeHours', {
                                        until: untilTrack >= 60
                                            ? t('realtime.untilTrackHM', { h: Math.floor(untilTrack / 60), m: untilTrack % 60 })
                                            : t('realtime.untilTrackM', { m: untilTrack }),
                                    });
                                })()}
                            </p>
                        ) : shareLocation ? (
                            <p className={cls("rl-info-notice")}>
                                {minutesUntilStart != null && minutesUntilStart > 0
                                    ? t('realtime.shareNoticePreStart', { n: minutesUntilStart })
                                    : t('realtime.shareNotice')}
                            </p>
                        ) : null}

                        {/* 목적지 */}
                        <div className={cls("rl-info-row")}>
                            <span className={cls("rl-info-label")}>{t('realtime.destinationLabel')}</span>
                            <span className={cls("rl-info-value")}>
                                {meetingLocation.name}
                                {meetingLocation.address ? (
                                    <span className={cls("rl-info-sub")}>{meetingLocation.address}</span>
                                ) : null}
                            </span>
                        </div>

                        {/* 도착 레이스 — 요약만. 참여자별 진행은 참여자 탭에 있습니다 */}
                        {isRaceWindow && progressRows.length > 0 && (
                            <div className={cls("rl-info-row")}>
                                <span className={cls("rl-info-label")}>{t('realtime.raceLabel')}</span>
                                <span className={cls("rl-info-value")}>
                                    {t('realtime.raceTitle', { n: progressRows.length })}
                                    {minutesUntilStart != null ? (
                                        <span className={cls("rl-info-sub")}>
                                            {minutesUntilStart > 0
                                                ? t('realtime.raceMinBefore', { n: minutesUntilStart })
                                                : minutesUntilStart === 0
                                                    ? t('realtime.raceStartNow')
                                                    : t('realtime.raceAfter', { n: Math.abs(minutesUntilStart) })}
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                        )}

                        {/* 지도 앱으로 열기 */}
                        <Button
                            block
                            variant="secondary"
                            onClick={() => setShowExternalMap(true)}
                        >
                            {t('meeting.otherMapApps')}
                        </Button>
                    </div>
                )}

                {/* === 길찾기 모드 === */}
                {viewMode === 'directions' && (
                    <div className={cls("rl-directions")}>
                        {!myLocation && (
                            <div className={cls("rl-dir-empty")}><IcPin size={14} /> {t('realtime.findingMyLocation')}</div>
                        )}
                        {myLocation && isRouteLoading && (
                            <div className={cls("rl-dir-empty")}><IcCompass /> {t('realtime.calculatingRoute')}</div>
                        )}
                        {myLocation && !isRouteLoading && !myRoute && (
                            <div className={cls("rl-dir-empty")}>{t('realtime.routeNotFound')}</div>
                        )}
                        {myRoute && (
                            <>
                                {!isListExpanded && myRoute.steps.length > 0 && (
                                    <div className={cls("rl-dir-preview")}>
                                        {myRoute.steps.slice(0, 2).map((s, i) => (
                                            <div key={i} className={cls("rl-step rl-step-mini")}>
                                                <span className={cls("rl-step-icon")}>{stepIcon(s.type)}</span>
                                                <span className={cls("rl-step-text")}>{s.instruction}</span>
                                                {s.distance > 0 && <span className={cls("rl-step-dist")}>{formatStepDistance(s.distance)}</span>}
                                            </div>
                                        ))}
                                        {myRoute.steps.length > 2 && (
                                            <button className={cls("rl-dir-more")} onClick={() => setIsListExpanded(true)}>
                                                +{t('realtime.moreSteps', { n: myRoute.steps.length - 2 })}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {isListExpanded && (
                                    <div className={cls("rl-dir-full")}>
                                        {myRoute.steps.length === 0 && (
                                            <div className={cls("rl-dir-empty")}>{t('race.noDirInfo')}</div>
                                        )}
                                        {myRoute.steps.map((s, i) => (
                                            <div key={i} className={cls("rl-step")}>
                                                <div className={cls("rl-step-icon-box")}>{stepIcon(s.type)}</div>
                                                <div className={cls("rl-step-body")}>
                                                    <div className={cls("rl-step-text")}>{s.instruction}</div>
                                                    {s.roadName && <div className={cls("rl-step-road")}>{s.roadName}</div>}
                                                </div>
                                                {s.distance > 0 && <div className={cls("rl-step-dist")}>{formatStepDistance(s.distance)}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* 참여자 모드 — 레이스 윈도우 여부에 따라 렌더 분기 */}
                {viewMode === 'participants' && (() => {
                    // 레이스 모드 보조 — userNo → race row 매핑
                    const raceByUser = new Map<number, RaceProgressRow>();
                    progressRows.forEach(r => raceByUser.set(r.userNo, r));
                    // 1~3위는 SVG 메달 아이콘(이모지 금지), 그 외는 순위 숫자 텍스트.
                    const medalIcon = (rank: number): React.ReactNode =>
                        rank <= 3 ? <IcMedal size={16} /> : `${rank}`;
                    const formatRaceDist = (m: number): string => m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`;

                    // 접힌 상태 — 레이스면 순위순 최상위 3, 아니면 기존 상태순
                    if (!isListExpanded) {
                        const rowsForMini = isRaceWindow && progressRows.length > 0
                            ? progressRows.slice(0, 3).map(r => {
                                const p = participants.find(pp => pp.id === r.userNo);
                                return { race: r, p };
                            })
                            : null;
                        return (
                            <div className={cls("rl-mini-list")}>
                                {rowsForMini
                                    ? rowsForMini.map(({ race, p }) => {
                                        if (!p) return null;
                                        const total = Math.max(race.startDistM, race.currentDistM, 1);
                                        const done = Math.max(0, race.startDistM - race.currentDistM);
                                        const pct = race.arrived ? 100 : Math.min(100, Math.max(0, (done / total) * 100));
                                        return (
                                            <div key={p.id} className={cls("rl-mini-item race")}>
                                                <span className={cls(`rl-race-medal rank-${Math.min(race.rank, 4)}`)}>{medalIcon(race.rank)}</span>
                                                <div className={cls("rl-mini-avatar arriving")}>
                                                    {p.profileImgUrl ? <img src={p.profileImgUrl} alt="" className={cls("rl-avatar-img")} /> : p.name[0]}
                                                    {!race.arrived && (
                                                        <span className={cls("rl-race-runner")} aria-hidden>
                                                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.89 19.38l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" /></svg>
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={cls("rl-mini-name")}>{p.name}{race.isMe && <span className={cls("rl-race-me-pill")}>{t('race.me')}</span>}</span>
                                                <div className={cls("rl-race-bar")}>
                                                    <div className={cls("rl-race-bar-fill")} style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className={cls("rl-race-dist")}>
                                                    {race.arrived ? <span className={cls("rl-race-arrived")}>{t('race.arrived')}</span> : formatRaceDist(race.currentDistM)}
                                                </span>
                                            </div>
                                        );
                                    })
                                    : sorted.slice(0, 3).map(p => {
                                        const sl = statusLabel(p.status);
                                        return (
                                            <div key={p.id} className={cls("rl-mini-item")}>
                                                <div className={cls(`rl-mini-avatar ${p.status}`)}>
                                                    {p.profileImgUrl ? <img src={p.profileImgUrl} alt="" className={cls("rl-avatar-img")} /> : p.name[0]}
                                                </div>
                                                <span className={cls("rl-mini-name")}>{p.name}</span>
                                                <span className={cls("rl-mini-status")} style={{ color: sl.color }}>{sl.icon} {p.distance !== '-' ? p.distance : sl.text}</span>
                                            </div>
                                        );
                                    })}
                                {participants.length > 3 && (
                                    <button className={cls("rl-mini-more")} onClick={() => setIsListExpanded(true)}>+{t('realtime.moreParticipants', { n: participants.length - 3 })}</button>
                                )}
                            </div>
                        );
                    }

                    // 펼친 상태 — 레이스면 순위순 전체, 상세 정보 포함
                    if (isRaceWindow && progressRows.length > 0) {
                        return (
                            <div className={cls("rl-full-list race")}>
                                {progressRows.map(race => {
                                    const p = participants.find(pp => pp.id === race.userNo);
                                    if (!p) return null;
                                    const total = Math.max(race.startDistM, race.currentDistM, 1);
                                    const done = Math.max(0, race.startDistM - race.currentDistM);
                                    const pct = race.arrived ? 100 : Math.min(100, Math.max(0, (done / total) * 100));
                                    return (
                                        <div key={p.id} className={cls(`rl-list-item race rank-${Math.min(race.rank, 4)} ${race.isMe ? 'is-me' : ''} ${race.isLast ? 'is-last' : ''}`)}>
                                            <div className={cls("rl-race-medal-box")}>
                                                <span className={cls(`rl-race-medal rank-${Math.min(race.rank, 4)}`)}>{medalIcon(race.rank)}</span>
                                            </div>
                                            <div className={cls("rl-list-avatar race")}>
                                                {p.profileImgUrl ? <img src={p.profileImgUrl} alt="" className={cls("rl-avatar-img")} /> : p.name[0]}
                                                {!race.arrived && (
                                                    <span className={cls("rl-race-runner")} aria-hidden>
                                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.89 19.38l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" /></svg>
                                                    </span>
                                                )}
                                            </div>
                                            <div className={cls("rl-list-info")}>
                                                <div className={cls("rl-list-name")}>
                                                    {p.name}
                                                    {race.isMe && <span className={cls("rl-race-me-pill")}>{t('race.me')}</span>}
                                                    {race.isLast && !race.arrived && <span className={cls("rl-race-last-pill")} title={t('race.last')}><IcTurtle size={13} /></span>}
                                                </div>
                                                <div className={cls("rl-race-bar-wide")}>
                                                    <div className={cls("rl-race-bar-fill")} style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                            <div className={cls("rl-race-dist-box")}>
                                                {race.arrived ? (
                                                    <span className={cls("rl-race-arrived")}>{t('race.arrived')}</span>
                                                ) : (
                                                    <>
                                                        <div className={cls("rl-race-dist-main")}>{formatRaceDist(race.currentDistM)}</div>
                                                        <div className={cls("rl-race-dist-sub")}>/ {formatRaceDist(race.startDistM)}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* 레이스 미참여(불참/미정/비공유)는 하단에 간략 표시 */}
                                {sorted.filter(p => !raceByUser.has(p.id)).map(p => {
                                    const sl = statusLabel(p.status);
                                    return (
                                        <div key={p.id} className={cls("rl-list-item inactive")}>
                                            <div className={cls(`rl-list-avatar ${p.status}`)}>
                                                {p.profileImgUrl ? <img src={p.profileImgUrl} alt="" className={cls("rl-avatar-img")} /> : p.name[0]}
                                            </div>
                                            <div className={cls("rl-list-info")}>
                                                <div className={cls("rl-list-name")}>{p.name}</div>
                                                <div className={cls("rl-list-detail")}>
                                                    <span style={{ color: sl.color, fontWeight: 600 }}>{sl.icon} {sl.text}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    }

                    // 펼친 상태 — 일반 모드 (기존)
                    return (
                        <div className={cls("rl-full-list")}>
                            {sorted.map((p, i) => {
                                const sl = statusLabel(p.status);
                                return (
                                    <div key={p.id} className={cls(`rl-list-item ${p.showOnMap ? '' : 'inactive'}`)}>
                                        <div className={cls(`rl-list-avatar ${p.status}`)} style={p.showOnMap ? { background: MARKER_COLORS[i % MARKER_COLORS.length] } : {}}>
                                            {p.profileImgUrl ? <img src={p.profileImgUrl} alt="" className={cls("rl-avatar-img")} /> : p.name[0]}
                                        </div>
                                        <div className={cls("rl-list-info")}>
                                            <div className={cls("rl-list-name")}>{p.name}</div>
                                            <div className={cls("rl-list-detail")}>
                                                <span style={{ color: sl.color, fontWeight: 600 }}>{sl.icon} {sl.text}</span>
                                                {p.showOnMap && <span className={cls("rl-list-sep")}>·</span>}
                                                {p.showOnMap && <span className={cls("rl-list-dist")}>{p.distance}</span>}
                                            </div>
                                        </div>
                                        {p.showOnMap && (
                                            <div className={cls("rl-list-eta")}>
                                                <div className={cls("rl-eta-text")}>{p.eta}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>

            {/* STOMP 디버그 콘솔 — 폰에서 logcat 대용 */}
            <button
                type="button"
                className={cls(`rl-debug-fab ${debugOpen ? 'on' : ''}`)}
                onClick={() => setDebugOpen(o => !o)}
                aria-label="STOMP debug"
            >
                {`DBG ${stompLogs.length}`}
            </button>
            {debugOpen && (
                <div className={cls("rl-debug-panel")} role="dialog" aria-label="STOMP debug">
                    <div className={cls("rl-debug-header")}>
                        <span className={cls("rl-debug-title")}>STOMP {stompLogs.length}</span>
                        <button
                            type="button"
                            className={cls(`rl-debug-btn ${copyState !== 'idle' ? 'on' : ''}`)}
                            onClick={copyStompLogs}
                            disabled={stompLogs.length === 0}
                        >
                            {copyState === 'ok' ? 'copied' : copyState === 'fail' ? 'fail' : 'copy'}
                        </button>
                        <button type="button" className={cls("rl-debug-btn")} onClick={() => setStompLogs([])}>clear</button>
                        <button type="button" className={cls("rl-debug-btn")} onClick={() => setDebugOpen(false)}>close</button>
                    </div>
                    <div className={cls("rl-debug-body")}>
                        {nativeSummary && (
                            <details className={cls("rl-debug-native")} open>
                                <summary>
                                    NATIVE STATS{nativeSummaryAt ? ` · ${new Date(nativeSummaryAt).toISOString().slice(11, 19)}` : ''}
                                </summary>
                                <pre className={cls("rl-debug-native-pre")}>{nativeSummary}</pre>
                            </details>
                        )}
                        {stompLogs.length === 0 && !nativeSummary && (
                            <div className={cls("rl-debug-empty")}>no log yet…</div>
                        )}
                        {stompLogs.map((l, i) => (
                            <div key={i} className={cls(`rl-debug-row tag-${l.tag.toLowerCase()}`)}>
                                <span className={cls("rl-debug-time")}>{l.time}</span>
                                <span className={cls("rl-debug-tag")}>{l.tag}</span>
                                <span className={cls("rl-debug-text")}>{l.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ExternalMapSheet
                visible={showExternalMap}
                placeName={meetingLocation.name}
                address={meetingLocation.address}
                lat={Number(meetingLocation.latitude) || 0}
                lng={Number(meetingLocation.longitude) || 0}
                onClose={() => setShowExternalMap(false)}
            />
        </div>
    );
};
